import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronDown, ChevronUp, Copy, FolderOpen, Pause, Play, RotateCcw, X } from 'lucide-react';
import { useUploadOrder, useUploadRecord, useUploadStore, useUploadSummary } from '@/stores/upload';
import { uploadManager } from './UploadManager';
import { IconButton, Button } from '@/components/Button';
import { Spinner } from '@/components/Misc';
import { cx, formatBytes } from '@/lib/format';
import { decideDuplicatePrompt, installDuplicateGate, resetDuplicatePrompts, useUploadPromptStore, type PendingPrompt } from '@/features/duplicates/uploadPrompt';
import { CompleteMetadataStep } from './CompleteMetadataStep'; // release2/B

/**
 * §3 upload tray; survives route changes. `docked` renders it as a flex child of the workspace BottomDock
 * (next to the selection toolbar); the guest page mounts it standalone, fixed bottom-right.
 */
export function UploadPanel({ docked = false }: { docked?: boolean }) {
  const order = useUploadOrder();
  const sum = useUploadSummary();
  const collapsed = useUploadStore((s) => s.panelCollapsed);
  const setCollapsed = useUploadStore((s) => s.setPanelCollapsed);
  const fileInput = useRef<HTMLInputElement>(null);
  // Release 1 F5: the "Already in your library" gate runs between hashing and the scheduler; the remembered
  // "apply to all" answer lasts for one batch (until the tray empties).
  useEffect(() => installDuplicateGate(), []);
  useEffect(() => {
    if (order.length === 0) resetDuplicatePrompts();
  }, [order.length]);
  if (order.length === 0) return null;
  const title = sum.paused ? `Paused${sum.pausedReason === 'offline' ? ' — offline' : sum.pausedReason === 'saveData' ? ' — data saver' : ''}` : sum.uploading > 0 ? `Uploading ${sum.uploading} of ${sum.total}` : sum.failed > 0 ? `${sum.failed} failed` : `${sum.done} uploaded`;
  return (
    <section aria-label="Uploads" className={cx('pointer-events-auto w-[360px] overflow-hidden rounded-xl border border-line bg-bg-elev shadow-xl', docked ? 'max-w-full shrink-0' : 'fixed right-4 bottom-4 z-40 max-w-[calc(100vw-2rem)]')}>
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{title}</div>
          {sum.uploading > 0 && (
            <div className="mt-1 h-1 w-full overflow-hidden rounded bg-bg-sunken" role="progressbar" aria-valuenow={Math.round(sum.fraction * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-accent transition-[width]" style={{ width: `${sum.fraction * 100}%` }} />
            </div>
          )}
        </div>
        {sum.uploading > 0 && (
          <IconButton label={sum.paused ? 'Resume all' : 'Pause all'} size="sm" onClick={() => (sum.paused ? uploadManager.resumeAll() : uploadManager.pauseAll('user'))}>
            {sum.paused ? <Play size={14} /> : <Pause size={14} />}
          </IconButton>
        )}
        <IconButton label={collapsed ? 'Expand' : 'Collapse'} size="sm" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </IconButton>
        <IconButton
          label="Clear finished"
          size="sm"
          onClick={() => {
            for (const id of order) {
              const r = useUploadStore.getState().records[id];
              if (r && (r.status === 'ready' || r.status === 'failed' || r.status === 'cancelled')) uploadManager.dismiss(id);
            }
          }}
        >
          <X size={14} />
        </IconButton>
      </header>
      {sum.pendingResume > 0 && (
        <div className="flex items-center gap-2 border-b border-line bg-warn/10 px-3 py-2 text-xs">
          <FolderOpen size={14} className="shrink-0 text-warn" />
          <span className="flex-1">Resume {sum.pendingResume} upload{sum.pendingResume === 1 ? '' : 's'} — re-select the files</span>
          <Button size="sm" onClick={() => void uploadManager.resumeWithHandles().then((n) => n === 0 && fileInput.current?.click())}>
            Choose…
          </Button>
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && void uploadManager.attachFiles(e.target.files)} />
        </div>
      )}
      <CompleteMetadataStep />{/* release2/B */}
      {!collapsed && (
        <ul className="max-h-72 overflow-y-auto">
          {order.map((id) => (
            <UploadRow key={id} id={id} />
          ))}
        </ul>
      )}
    </section>
  );
}

function UploadRow({ id }: { id: string }) {
  const r = useUploadRecord(id);
  const prompt = useUploadPromptStore((s) => s.pending[id]);
  if (!r) return null;
  if (prompt) return <DuplicatePromptRow id={id} prompt={prompt} preview={r.preview?.blobUrl ?? null} />;
  const pct = r.status === 'hashing' ? (r.hash_progress ?? 0) * 100 : r.status === 'uploading' || r.status === 'paused' ? (r.size ? (r.bytesSent / r.size) * 100 : 0) : r.status === 'processing' ? (r.processing_progress ?? 0) : r.status === 'completing' || r.status === 'ready' ? 100 : 0;
  const label =
    r.status === 'pending' ? (r.needsFile ? 'Waiting for file' : 'Queued') : r.status === 'hashing' ? `Checking ${Math.round(pct)}%` : r.status === 'initiating' ? 'Starting…' : r.status === 'uploading' ? `${formatBytes(r.bytesSent)} / ${formatBytes(r.size)}` : r.status === 'completing' ? 'Finishing…' : r.status === 'processing' ? `Processing ${Math.round(pct)}%` : r.status === 'ready' ? 'Done' : r.status === 'paused' ? 'Paused' : r.status === 'cancelled' ? 'Cancelled' : (r.error?.message ?? 'Failed');
  const failed = r.status === 'failed';
  return (
    <li className="group flex items-center gap-2 border-b border-line px-3 py-2 last:border-b-0" data-upload-id={id} data-status={r.status}>
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-bg-sunken">{r.preview?.blobUrl ? <img src={r.preview.blobUrl} alt="" className="h-full w-full object-cover" /> : null}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium" title={r.name}>
          {r.name}
        </div>
        <div className={cx('truncate text-xs', failed ? 'text-danger' : 'text-fg-subtle')}>{label}</div>
        {r.status !== 'ready' && r.status !== 'failed' && r.status !== 'cancelled' && (
          <div className="mt-1 h-1 w-full overflow-hidden rounded bg-bg-sunken">
            <div className={cx('h-full transition-[width] duration-150', r.status === 'processing' ? 'bg-info' : 'bg-accent')} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {r.status === 'ready' && <Check size={14} className="text-ok" />}
        {failed && <AlertCircle size={14} className="text-danger" />}
        {(r.status === 'initiating' || r.status === 'completing') && <Spinner size={12} />}
        {failed && r.error?.retryable && (
          <IconButton label="Retry" size="sm" onClick={() => uploadManager.retry(id)}>
            <RotateCcw size={13} />
          </IconButton>
        )}
        <IconButton
          label={failed || r.status === 'ready' ? 'Dismiss' : 'Cancel'}
          size="sm"
          className={cx(!(failed || r.status === 'ready') && 'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100')}
          onClick={() => (failed || r.status === 'ready' ? uploadManager.dismiss(id) : uploadManager.cancel(id))}
        >
          <X size={13} />
        </IconButton>
      </div>
    </li>
  );
}

/** F5 — the file already exists (same sha256): Skip is the default; "apply to all" answers the rest of the batch. */
function DuplicatePromptRow({ id, prompt, preview }: { id: string; prompt: PendingPrompt; preview: string | null }) {
  const [all, setAll] = useState(false);
  const first = prompt.matches[0];
  const more = prompt.matches.length - 1;
  return (
    <li className="border-b border-line bg-warn/10 px-3 py-2 last:border-b-0" data-upload-id={id} data-status="duplicate-prompt">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-bg-sunken">{preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : first?.thumbnails['256'] ? <img src={first.thumbnails['256']} alt="" className="h-full w-full object-cover" /> : null}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium" title={prompt.name}>{prompt.name}</div>
          <div className="flex items-center gap-1 truncate text-xs text-warn">
            <Copy size={11} className="shrink-0" /> Already in your library{first ? ` as “${first.title}”` : ''}{more > 0 ? ` (+${more})` : ''}
          </div>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <Button size="sm" variant="primary" autoFocus onClick={() => decideDuplicatePrompt(id, 'skip', all)}>Skip</Button>
        <Button size="sm" onClick={() => decideDuplicatePrompt(id, 'upload', all)}>Upload anyway</Button>
        <label className="ml-auto flex items-center gap-1 text-xs text-fg-subtle">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Apply to all
        </label>
      </div>
    </li>
  );
}
